import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  fallbackProductsByCategory,
  loadCatalogProducts,
  productCategoryIds,
} from "./catalog-data";
import { setDatabaseProductImages } from "./product-images";

type ProductsByCategory = Record<string, string[]>;

const ProductCatalogContext = createContext<ProductsByCategory>(fallbackProductsByCategory);

function mergeProducts(rows: Awaited<ReturnType<typeof loadCatalogProducts>>): ProductsByCategory {
  setDatabaseProductImages(rows);
  return Object.fromEntries(productCategoryIds.map((category) => {
    const names = [...fallbackProductsByCategory[category], ...rows.filter((row) => row.category === category).map((row) => row.name)];
    return [category, Array.from(new Map(names.map((name) => [name.toLocaleLowerCase(), name])).values())];
  })) as ProductsByCategory;
}

export function ProductCatalogProvider({ children }: { children: ReactNode }) {
  const [importedProducts, setImportedProducts] = useState<Awaited<ReturnType<typeof loadCatalogProducts>>>([]);

  useEffect(() => {
    let active = true;
    const refresh = () => {
      void loadCatalogProducts().then((products) => {
        if (active) setImportedProducts(products);
      }).catch(() => {
        // The built-in catalogue remains available while the remote catalogue is unavailable.
      });
    };
    refresh();
    window.addEventListener("kitanego:catalog-updated", refresh);
    return () => {
      active = false;
      window.removeEventListener("kitanego:catalog-updated", refresh);
    };
  }, []);

  const value = useMemo(() => mergeProducts(importedProducts), [importedProducts]);
  return <ProductCatalogContext.Provider value={value}>{children}</ProductCatalogContext.Provider>;
}

export function useProductCatalog() {
  return useContext(ProductCatalogContext);
}
